import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  addContact,
  addContactUserPortal,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { Person } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { emailRegex } from '../../../../utils/regular-expressions';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface AddContactProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  userView?: boolean;
  contactList: Person[];
  selectedLabel: string;
  groupMembers: string[];
  page: number;
  pageSize: number;
  changeLogRoles: string[];
}

export default function AddContactDialog({
  open,
  handleClose,
  userEmail,
  userView,
  contactList,
  selectedLabel,
  groupMembers,
  page,
  pageSize,
  changeLogRoles,
}: AddContactProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [hasError, setHasError] = useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      addContact(
        userEmail,
        {
          emailAddresses: [{ value: email }],
          memberships: [
            {
              contactGroupMembership: {
                contactGroupResourceName:
                  selectedLabel === 'contactGroups/all'
                    ? 'contactGroups/myContacts'
                    : selectedLabel,
              },
            },
          ],
          names: [{ familyName: lastName, givenName: firstName }],
        },
        changeLogMessage
      ),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Contact added successfully!',
      });

      const newList = [...contactList, data];
      queryClient.setQueryData(
        [
          UserKeys.Contacts,
          userEmail,
          selectedLabel,
          groupMembers,
          page,
          pageSize,
        ],
        newList
      );
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue adding this contact. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.personal_contacts
          .create_personal_contact,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.personalContacts,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const { mutate: userPortalMutate, isPending: userPortalPending } =
    useMutation({
      mutationFn: () =>
        addContactUserPortal({
          emailAddresses: [{ value: email }],
          memberships: [
            {
              contactGroupMembership: {
                contactGroupResourceName:
                  selectedLabel === 'contactGroups/all'
                    ? 'contactGroups/myContacts'
                    : selectedLabel,
              },
            },
          ],
          names: [{ familyName: lastName, givenName: firstName }],
        }),
      onSuccess: (data) => {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Contact added successfully!',
        });

        const newList = [...contactList, data];
        queryClient.setQueryData(
          [
            UserKeys.Contacts,
            userEmail,
            selectedLabel,
            groupMembers,
            page,
            pageSize,
          ],
          newList
        );
        setChangeLogMessage('');
        handleClose();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue adding this contact. Please refresh and try again.',
        });
      },
    });

  function handleSubmit() {
    if (!emailRegex.test(email)) {
      setHasError(true);
    } else {
      setHasError(false);
      if (
        !userView &&
        isAbleToSeeUIElement(changeLogRoles, UserRoles.AddContacts.value)
      ) {
        setOpenChangeLogDialog(true);
      } else if (userView) {
        userPortalMutate();
      } else {
        mutate();
      }
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Create Contact"
      dialogContent={
        <Stack gap={2}>
          <TextField
            id="email"
            label="Email *"
            placeholder="Enter email address"
            fullWidth
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={hasError}
            helperText={hasError ? 'Must be a valid email address.' : ''}
            autoFocus
          />
          <TextField
            id="firstName"
            label="First Name"
            fullWidth
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
          <TextField
            id="lastName"
            label="Last Name"
            fullWidth
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Add Contact',
        action: handleSubmit,
        loading: isPending || userPortalPending,
        disabled: email.length === 0,
      }}
    />
  );
}
